import {
    type AnnualCashFlow,
    RetirementCalculator as FinancialRetirementCalculator,
    type RetirementCalculatorInputs as RetirementCalculatorInputsData,
    type RetirementCalculatorOutputs,
} from '@fruitful/financial-calculators';
import React, { useMemo, useState } from 'react';
import {
    BodyLargeRegular,
    H2Emphasized,
    Spinner,
    SpinnerSize,
    useToastController,
    YStack,
} from 'smoothie';

import { PageContentContainer } from '../../common/components/GroveContainers';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { useRetirementCalculatorInputs } from '../hooks/useRetirementCalculatorInputs';
import { AnnualCashFlows } from './AnnualCashFlows';
import { RequiredRetirementContributions } from './RequiredRetirementContributions';
import { RetirementCalculatorHeader } from './RetirementCalculatorHeader';
import { RetirementCalculatorInputs } from './RetirementCalculatorInputs';
import { RetirementCalculatorInputsModal } from './RetirementCalculatorInputsModal';
import { RetirementContributionsSimulationModal } from './RetirementContributionsSimulationModal';

export const RetirementCalculator = () => {
    const toast = useToastController();
    const { retirementCalculatorInputs, isLoading: isLoadingInputs } =
        useRetirementCalculatorInputs();
    const [outputs, setOutputs] = useState<RetirementCalculatorOutputs | null>(
        null,
    );
    const [isCalculating, setIsCalculating] = useState(false);
    const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
    const [isSimulationModalOpen, setIsSimulationModalOpen] = useState(false);
    const [isInputsModalOpen, setIsInputsModalOpen] = useState(true); // Default to open
    const [formData, setFormData] = useState<RetirementCalculatorInputsData>(
        retirementCalculatorInputs,
    );

    // Get the annual cash flows for the selected amount
    const annualCashFlows = useMemo((): AnnualCashFlow[] | null => {
        if (!outputs || selectedAmount === null) {
            return null;
        }

        return (
            outputs.annualSavingsOutcomes[selectedAmount]
                ?.averageCashFlowByYear ?? null
        );
    }, [outputs, selectedAmount]);

    const handleCalculate = async (inputs: RetirementCalculatorInputsData) => {
        setIsCalculating(true);
        setOutputs(null);
        setSelectedAmount(null);
        setFormData(inputs); // Store the form data
        setIsInputsModalOpen(false); // Close inputs panel

        try {
            // Use setTimeout to allow the UI to update before starting the calculation
            await new Promise<void>((resolve) => {
                setTimeout(resolve, 0);
            });

            const calculator = new FinancialRetirementCalculator(inputs);
            const results = calculator.calculate();

            setOutputs(results);

            // Automatically select the target required savings level
            const targetAmount = results.requiredAnnualRetirementSavings;
            setSelectedAmount(targetAmount);

            toast.show(
                'Retirement calculation completed!',
                MutationSuccessToastOptions,
            );
        } catch (error) {
            toast.show(
                'Error calculating retirement',
                MutationErrorToastOptions(error),
            );
        } finally {
            setIsCalculating(false);
        }
    };

    const handleDrilldown = (amount: number) => {
        setSelectedAmount(amount);
    };

    // Loading state for inputs
    if (isLoadingInputs) {
        return (
            <PageContentContainer width="100%">
                <YStack gap={16} alignItems="center" padding={24}>
                    <H2Emphasized>Loading...</H2Emphasized>
                    <BodyLargeRegular>
                        Gathering your financial data for retirement planning.
                    </BodyLargeRegular>
                    <Spinner size={SpinnerSize.SMALL} />
                </YStack>
            </PageContentContainer>
        );
    }

    // Loading state for calculation
    if (isCalculating) {
        return (
            <PageContentContainer width="100%">
                <YStack gap={16} alignItems="center" padding={24}>
                    <H2Emphasized>Calculating...</H2Emphasized>
                    <BodyLargeRegular>
                        Running Monte Carlo simulations. This may take a few
                        moments.
                    </BodyLargeRegular>
                    <Spinner size={SpinnerSize.SMALL} />
                </YStack>
            </PageContentContainer>
        );
    }

    // Show inputs when no calculation has been done yet
    if (!outputs || selectedAmount === null) {
        return (
            <PageContentContainer flex={1} height="100%">
                <RetirementCalculatorInputs
                    onCalculate={handleCalculate}
                    isCalculating={isCalculating}
                    initialData={formData}
                />
            </PageContentContainer>
        );
    }

    return (
        <YStack width="100%">
            <RetirementCalculatorHeader
                selectedAmount={selectedAmount}
                onEditInputs={() => {
                    setIsInputsModalOpen(true);
                }}
                onRecalculate={() => {
                    void handleCalculate(formData);
                }}
                onViewAllSimulations={() => {
                    setIsSimulationModalOpen(true);
                }}
                onEditSelectedAmount={() => {
                    setIsSimulationModalOpen(true);
                }}
            />

            <RequiredRetirementContributions outputs={outputs} />

            {/* Cash Flows Component with Tabs */}
            {annualCashFlows && annualCashFlows.length > 0 && (
                <AnnualCashFlows
                    annualCashFlows={annualCashFlows}
                    selectedAmount={selectedAmount}
                />
            )}

            {/* Edit Inputs Modal */}
            <RetirementCalculatorInputsModal
                isOpen={isInputsModalOpen}
                onClose={() => {
                    setIsInputsModalOpen(false);
                }}
                onCalculate={handleCalculate}
                isCalculating={isCalculating}
                initialData={formData}
            />

            {/* Simulation Modal */}
            {isSimulationModalOpen && (
                <RetirementContributionsSimulationModal
                    outputs={outputs}
                    onSelectAmount={handleDrilldown}
                    isOpen={isSimulationModalOpen}
                    onClose={() => {
                        setIsSimulationModalOpen(false);
                    }}
                />
            )}
        </YStack>
    );
};
